import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Container, Typography, Button, TextField, Box, InputAdornment } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import LinearProgress from '@mui/material/LinearProgress';

const HomeContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));

const WelcomeBlock = styled(Box)(({ theme }) => ({
  textAlign: 'left',
  marginBottom: theme.spacing(4),
  background: 'linear-gradient(90deg, #7d1aed 0%, rgb(110, 58, 170) 100%)',
  padding: theme.spacing(4),
  boxShadow: theme.shadows[3],
  borderRadius: '20px',
}));

const FeaturedSection = styled(Box)(({ theme }) => ({
  textAlign: 'left',
  marginBottom: theme.spacing(4),
}));

const WhyInvestSection = styled(Box)(({ theme }) => ({
  textAlign: 'left',
  marginBottom: theme.spacing(4),
  backgroundColor: 'rgb(31 31 31)',
  padding: theme.spacing(4),
  boxShadow: theme.shadows[3],
  borderRadius: '20px',
  color: 'white',
}));

const SearchFilters = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const FilterButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(0.5),
  borderRadius: '20px',
  backgroundColor: 'rgb(31, 31, 31)',
  color: 'white',
}));

const MovieCard = styled(Box)(({ theme }) => ({
  display: 'inline-block',
  margin: theme.spacing(1),
  textAlign: 'center',
  color: 'white',
  width: '300px', // Increased from 150px to match the "All Movies" page
  padding: theme.spacing(2), // Added padding for better spacing
}));

const MoviePoster = styled('img')(({ theme }) => ({
  width: '100%', // Full width of the card
  height: '450px', // Increased from 225px to match the "All Movies" page
  objectFit: 'cover',
  borderRadius: '10px',
  marginBottom: theme.spacing(1),
}));

function Home() {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('All');

  // Static movie data
  const staticMovies = [
    {
      id: 1,
      name: 'Stree 2: Sarkate Ka Aatank',
      genre: 'Horror',
      image: 'https://cinevest.vercel.app/_next/image?url=https%3A%2F%2Ffirebasestorage.googleapis.com%2Fv0%2Fb%2Fgaadhaar-7ff3f.appspot.com%2Fo%2Fposters%252F1728662935702_MV5BZTEyYzVjYjEtNWYzZi00NWRhLTgyZjUtNDlkZjBiNWIxYTEwXkEyXkFqcGc%2540._V1_.jpg%3Falt%3Dmedia%26token%3Db86c445e-6b3b-43ba-b5b9-7e3a19b8c003&w=384&q=75',
      currentFunding: 285000, // ₹28,50,000
      targetFunding: 5000000, // ₹50,00,000
    },
    {
      id: 2,
      name: 'Joker: Folie à Deux',
      genre: 'Action',
      image: 'https://cinevest.vercel.app/_next/image?url=https%3A%2F%2Ffirebasestorage.googleapis.com%2Fv0%2Fb%2Fgaadhaar-7ff3f.appspot.com%2Fo%2Fposters%252F1728663027256_deadpoolandwolverine_lob_crd_03.jpg%3Falt%3Dmedia%26token%3D4f5e0200-d588-4fed-bd1c-711fbed95b12&w=384&q=75',
      currentFunding: 10000, // ₹10,00,000
      targetFunding: 30000000, // ₹30,00,000
    },
    {
      id: 3,
      name: 'Deadpool & Wolverine',
      genre: 'Sci-Fi',
      image: 'https://cinevest.vercel.app/_next/image?url=https%3A%2F%2Ffirebasestorage.googleapis.com%2Fv0%2Fb%2Fgaadhaar-7ff3f.appspot.com%2Fo%2Fposters%252F1728725362909_medium-joker-movie-poster-for-room-office-13-inch-x-19-inch-original-imafk9kg4hczamuz.webp%3Falt%3Dmedia%26token%3D38f922ca-4d0e-4c84-b131-3e021e09dbd6&w=384&q=75',
      currentFunding: 10000, // ₹10,00,000
      targetFunding: 10000000, // ₹10,00,000
    },
  ];

  // Static genres
  const staticGenres = ['All', 'Action', 'Comedy', 'Drama', 'Sci-Fi', 'Horror', 'Romance', 'Thriller', 'Documentary'];

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleGenreChange = (genre) => {
    setSelectedGenre(genre);
  };

  // Filter movies based on search term and selected genre
  const filteredMovies = staticMovies.filter((movie) => {
    const matchesSearchTerm = movie.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesGenre = selectedGenre === 'All' || movie.genre === selectedGenre;
    return matchesSearchTerm && matchesGenre;
  });

  // Calculate progress percentage
  const calculateProgress = (current, target) => {
    if (target === 0) return 0;
    return Math.min((current / target) * 100, 100); // Cap at 100%
  };

  // Render movie cards
  const renderMovies = (movies) => (
    <Box display="flex" flexWrap="wrap" justifyContent="center">
      {movies.map((movie) => (
        <MovieCard key={movie.id}>
          <Link to={`/movie/${movie.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
            <MoviePoster src={movie.image} alt={movie.name} />
            <Typography variant="body2">{movie.name}</Typography>
            <Typography variant="caption">
              {movie.genre} {movie.releaseDate}
            </Typography>
            <Typography variant="body2" gutterBottom>
              ₹{movie.currentFunding.toLocaleString()} / ₹{movie.targetFunding.toLocaleString()}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={calculateProgress(movie.currentFunding, movie.targetFunding)}
              sx={{
                width: '100%',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#7d1aed', // Match the purple theme
                },
              }}
            />
          </Link>
        </MovieCard>
      ))}
    </Box>
  );

  return (
    <HomeContainer maxWidth="lg">
      {/* Welcome Block */}
      <WelcomeBlock>
        <Typography variant="h2" component="h1" gutterBottom sx={{ color: 'white' }}>
          Welcome to Cinevest
        </Typography>
        <Typography variant="h6" component="p" gutterBottom sx={{ color: 'white' }}>
          Invest in the Future of Cinema
        </Typography>
        <Link to="/movie">
          <Button
            variant="outlined"
            color="primary"
            sx={{
              borderRadius: '50px',
              backgroundColor: 'white',
              color: 'rgb(31 31 31)',
              '&:hover': {
                backgroundColor: 'rgb(200, 200, 200)',
              },
            }}
          >
            Explore Movies
          </Button>
        </Link>
      </WelcomeBlock>

      {/* Featured Movies (Now showing all movies with larger cards) */}
      <FeaturedSection>
        <Typography variant="h4" component="h2" gutterBottom sx={{ color: 'white' }}>
          🔥 Hot Right Now
        </Typography>
        {renderMovies(filteredMovies)}
      </FeaturedSection>

      {/* Why Invest Section */}
      <WhyInvestSection>
        <Typography variant="h4" component="h2" gutterBottom>
          Why Invest in Movies?
        </Typography>
        <ul>
          <li style={{ marginBottom: '15px' }}>Be part of the creative process</li>
          <li style={{ marginBottom: '15px' }}>Potential for high returns</li>
          <li style={{ marginBottom: '15px' }}>Exclusive behind-the-scenes access</li>
          <li style={{ marginBottom: '15px' }}>Support independent filmmakers</li>
        </ul>
        <Button
          variant="outlined"
          color="primary"
          sx={{
            borderRadius: '50px',
            backgroundColor: 'rgb(125, 26, 237)',
            color: 'white',
          }}
        >
          Learn More
        </Button>
      </WhyInvestSection>

      {/* Search and Filters */}
      <SearchFilters>
        <TextField
          variant="outlined"
          placeholder="Search movies..."
          fullWidth
          margin="normal"
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'white' }} />
              </InputAdornment>
            ),
            sx: {
              backgroundColor: 'rgb(31, 31, 31)',
              color: 'white',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgb(31, 31, 31)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgb(31, 31, 31)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgb(31, 31, 31)',
              },
            },
          }}
        />
        <Box display="flex" justifyContent="flex-start" flexWrap="wrap">
          {staticGenres.map((genre, index) => (
            <FilterButton
              key={index}
              variant="contained"
              onClick={() => handleGenreChange(genre)}
              sx={{
                backgroundColor: selectedGenre === genre ? 'rgb(125, 26, 237)' : 'rgb(31, 31, 31)',
                '&:hover': {
                  backgroundColor: selectedGenre === genre ? 'rgb(100, 21, 190)' : 'rgb(31, 31, 31)',
                },
              }}
            >
              {genre}
            </FilterButton>
          ))}
        </Box>
        {renderMovies(filteredMovies)}
      </SearchFilters>
    </HomeContainer>
  );
}

export default Home;